import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * Admin Schema
 * Represents an admin user for the platform
 */

// Provider sub-schema
export const AdminProviderSchema = z.object({
  name: z.string().optional(),
  id: z.string().optional(),
  organization: z.string().optional(),
  role: z.string().optional(),
});

export type AdminProvider = z.infer<typeof AdminProviderSchema>;

// Onboarding sub-schema for tracking tour completion
export const AdminOnboardingSchema = z.object({
  completedTours: z.array(z.string()).default([]),
  toursDismissed: z.boolean().default(false),
});

export type AdminOnboarding = z.infer<typeof AdminOnboardingSchema>;

// One-shot "finish setting up" nudge (sent by the onboarding nudge cron ~48h
// after a stalled self-serve signup; sentAt set = never nudge again).
export const AdminOnboardingNudgeSchema = z.object({
  sentAt: z.coerce.date().optional(),
  stage: z.string().optional(),
});

export type AdminOnboardingNudge = z.infer<typeof AdminOnboardingNudgeSchema>;

// State-driven organizer nudges (INT-0041): the per-admin send log the hourly
// sweep reads for its caps (3 per 30 days, 72h apart, one follow-up per
// issue) plus the owner's pause switch. `scope` = organization id or
// 'account' (no org yet). Platform-written only; never defaulted on the
// model (absent = never nudged, not paused).
export const AdminOnboardingNudgeSendSchema = z.object({
  template: z.string(),
  scope: z.string(),
  sentAt: z.coerce.date(),
  status: z.enum(['sent', 'failed']),
  messageId: z.string().optional(),
  snapshot: z.record(z.unknown()).optional(),
});

export const AdminOnboardingNudgesSchema = z.object({
  pausedAt: z.coerce.date().optional(),
  sent: z.array(AdminOnboardingNudgeSendSchema).default([]),
});

export type AdminOnboardingNudgeSend = z.infer<typeof AdminOnboardingNudgeSendSchema>;
export type AdminOnboardingNudges = z.infer<typeof AdminOnboardingNudgesSchema>;

// Acquisition attribution (funnel analytics, 2026-09-14): first-touch ad/CTA
// params captured on the landing site, carried through /sign-up, and stamped
// server-side at registration — then snapshotted onto the Organization at
// create. All optional, no defaults; values are sanitized/capped in
// apps/api/api/utils/acquisition.ts (URL params are attacker-controlled).
export const AcquisitionSchema = z.object({
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
  gclid: z.string().optional(),
  fbclid: z.string().optional(),
  // Manual referral code (?ref=...).
  ref: z.string().optional(),
  // The landing CTA slug — preserved when ad utm_* params override utm_campaign.
  cta: z.string().optional(),
  landingPath: z.string().optional(),
  capturedAt: z.coerce.date().optional(),
});

export type Acquisition = z.infer<typeof AcquisitionSchema>;

// Access scope sub-schema
export const AccessScopeSchema = z.object({
  level: z.enum(['tournament', 'league', 'division']),
  refId: z.string(),  // ObjectId as string
});

export type AccessScope = z.infer<typeof AccessScopeSchema>;

export const AdminSchema = z.object({
  _id: objectId,
  // Basic info
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  password: z.string().optional(),
  // Signup activation flow (signup.controller.ts): `code` is the emailed
  // activation code; `status` goes 'pending' → 'activated'.
  status: z.string().optional(),
  code: z.string().optional(),
  // Password-reset / set-password flow (Clerk migration). bcrypt password above.
  resetPasswordToken: z.string().optional(),
  resetPasswordExpires: z.coerce.date().optional(),
  // Provider info
  provider: AdminProviderSchema.optional(),
  // Onboarding state
  onboarding: AdminOnboardingSchema.optional(),
  onboardingNudge: AdminOnboardingNudgeSchema.optional(),
  onboardingNudges: AdminOnboardingNudgesSchema.optional(),
  // First-touch acquisition attribution (set once at signup, never overwritten).
  acquisition: AcquisitionSchema.optional(),
  // Rolling activity marker for the members page's "Last Active" column —
  // stamped (throttled) by requireAdminAuth on any authenticated admin
  // request. Optional, NO default: absent means "no activity recorded since
  // the field shipped" and readers fall back to `updated`. Never read this
  // for authorization decisions.
  lastActiveAt: z.coerce.date().optional(),
  // Access scopes for permission control
  accessScopes: z.array(AccessScopeSchema).default([]),
  // Refs
  organizations: z.array(objectId).default([]),
  players: z.array(z.string()).default([]),
  ...timestampFields,
});

export type Admin = z.infer<typeof AdminSchema>;

// Metadata for Admin fields (for Mongoose refs and UI)
export const adminMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  email: { label: 'Email', editable: true },
  firstName: { label: 'First Name', editable: true },
  lastName: { label: 'Last Name', editable: true },
  password: { hidden: true },
  status: { label: 'Status', editable: true },
  code: { label: 'Code', hidden: true },
  resetPasswordToken: { hidden: true },
  resetPasswordExpires: { hidden: true },
  provider: { label: 'Provider', hidden: true },
  onboarding: { label: 'Onboarding', hidden: true },
  onboardingNudge: { label: 'Onboarding Nudge', hidden: true },
  onboardingNudges: { label: 'Onboarding Nudges', hidden: true },
  acquisition: { label: 'Acquisition', hidden: true },
  accessScopes: { label: 'Access Scopes', hidden: true },
  organizations: { ref: 'Organization', label: 'Organizations' },
  players: { ref: 'Player', label: 'Players' },
  created: { hidden: true },
  updated: { hidden: true },
  lastActiveAt: { hidden: true },
};
